import {useNavigate, useLocation} from 'react-router-dom';
import {useState, useEffect} from 'react';
import {ShoppingCart, Eye} from 'lucide-react';
import {ProductCardProps} from '../types';
import {useShopping} from '../contexts/ShoppingContext';
import {ImagePreloader} from '../services/imagePreloader';
import colorMap from '../data/colors.json';
import {formatPrice} from "../utils/formatPrice.ts";

export function ProductCard({product}: ProductCardProps) {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [hoveredColorImage, setHoveredColorImage] = useState<string | null>(null);
    const {dispatch} = useShopping();
    const navigate = useNavigate();
    const location = useLocation();

    const minOrderQty = product.minOrderQty && product.minOrderQty > 1 ? product.minOrderQty : 1;

    const has_stock = product.allowBackorder || product.allowOnDemand ||
                      product.variations.find(v => (v.stock ?? 0) >= minOrderQty) ||
                      (product.variations.length === 0 && (product.stock ?? 0) >= minOrderQty);

    // Preload hover image on component mount for instant hover effect
    useEffect(() => {
        if (product.images.length > 1) {
            ImagePreloader.preloadImage(product.images[1]).catch(console.warn);
        }
    }, [product.images]);

    const handleCardClick = () => {
        // Preserve department and category IDs from current URL
        const searchParams = new URLSearchParams(location.search);
        const queryString = searchParams.toString();
        navigate(`/product/${product.id}${queryString ? `?${queryString}` : ''}`);
    };

    const handleMouseEnter = () => {
        setIsHovered(true);
        // Switch to second image on hover if available and no color is hovered
        if (product.images.length > 1 && !hoveredColorImage) {
            setCurrentImageIndex(1);
        }
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
        // Return to first image when not hovering
        setCurrentImageIndex(0);
        setHoveredColorImage(null);
    };

    const handleColorHover = (e: React.MouseEvent, color: string) => {
        e.stopPropagation();
        // Reset card hover state when color is hovered
        setIsHovered(false);
        setCurrentImageIndex(0);
        const variation = product.variations.find(v => v.color === color);
        if (variation && variation.images && variation.images.length > 0) {
            const image = variation.images[0];
            setHoveredColorImage(typeof image === 'string' ? image : image.url);
        }
    };

    const handleColorLeave = (e: React.MouseEvent) => {
        e.stopPropagation();
        setHoveredColorImage(null);
        // When leaving color swatch, revert to appropriate image
        if (isHovered && product.images.length > 1) {
            setCurrentImageIndex(1);
        } else {
            setCurrentImageIndex(0);
        }
    };

    const handleQuickView = (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch({type: 'OPEN_QUICK_VIEW', payload: product});
    };

    const handleQuickAdd = (e: React.MouseEvent) => {
        e.stopPropagation();

        // For products with variations
        if (product.variations.length > 0) {
            let variationToAdd;

            if (product.allowBackorder || product.allowOnDemand) {
                // If backorder or on-demand is allowed, use first variation
                variationToAdd = product.variations[0];
            } else {
                // Find first variation with enough stock for min order quantity
                variationToAdd = product.variations.find(v => (v.stock ?? 0) >= minOrderQty);
            }

            if (variationToAdd) {
                dispatch({
                    type: 'ADD_TO_CART',
                    payload: {product, variation: variationToAdd, quantity: minOrderQty}
                });
            }
        } else if (product.allowBackorder || product.allowOnDemand || (product.stock ?? 0) >= minOrderQty) {
            // For products without variations, create a default variation
            const defaultVariation = {
                id: `${product.id}-default`,
                color: "",
                size: "",
                price: product.basePrice,
                stock: product.stock || 0,
                images: product.images
            };
            dispatch({
                type: 'ADD_TO_CART',
                payload: {product, variation: defaultVariation, quantity: minOrderQty}
            });
        }
    };

    return (
        <article
            className="group bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 flex flex-col h-full"
            onClick={handleCardClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            aria-label={`${product.name} product card`}
        >
            {/* Image Section */}
            <div className="relative aspect-square overflow-hidden">
                <img
                    src={hoveredColorImage || product.images[currentImageIndex]}
                    alt={`${product.name}${hoveredColorImage ? ' - Color variation' : ''}`}
                    className={`w-full h-full object-cover transition-transform duration-500 ${
                        isHovered && product.images.length > 1
                            ? 'scale-110'
                            : 'group-hover:scale-105'
                    }`}
                />

                {/* Image Indicators */}
                {product.images.length > 1 && (
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                        {product.images.map((_, index) => (
                            <button
                                key={index}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentImageIndex(index);
                                }}
                                className={`w-2 h-2 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800 ${
                                    index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                                }`}
                                aria-label={`View image ${index + 1} of ${product.images.length}`}
                                aria-current={index === currentImageIndex}
                            />
                        ))}
                    </div>
                )}

                {/* Hover Actions */}
                <div
                    className={`absolute inset-0 bg-black/20 flex items-center justify-center transition-opacity duration-300 pointer-events-none ${
                        isHovered ? 'opacity-100' : 'opacity-0'
                    }`}>
                    <div className="flex space-x-2 pointer-events-auto">
                        <button
                            onClick={handleQuickView}
                            className="bg-white text-gray-900 p-2 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            aria-label={`Quick view ${product.name}`}
                        >
                            <Eye className="w-4 h-4" aria-hidden="true"/>
                        </button>
                        <button
                            onClick={handleQuickAdd}
                            className="
                                bg-blue-600 text-white p-2 rounded-full
                                hover:bg-blue-700
                                transition-colors
                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2

                                disabled:bg-gray-300
                                disabled:text-gray-500
                                disabled:cursor-not-allowed
                              "
                            aria-label={`Quick add ${product.name} to cart`}
                            disabled={!has_stock}
                        >
                            <ShoppingCart className="w-4 h-4" aria-hidden="true"/>
                        </button>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="p-4 flex-1 flex flex-col">
                <h2 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {product.name}
                </h2>

                {/* Price and Variations */}
                <div className="flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="text-lg font-bold text-gray-900">
                          ${formatPrice(product.variations[0]?.price || product.basePrice)}
                        </span>
                        {product.variations.length > 1 ? (
                            <span className="text-xs text-gray-500 mt-1">
                                +{product.variations.length - 1} variants
                            </span>
                        ) : product.variations.length === 0 && product.stock !== null && product.stock !== undefined ? (
                            // Show stock for products with no variations
                            product.stock > 0 || !product.allowOnDemand ? (
                                <span className={`text-xs mt-1 ${
                                    product.stock > 0
                                        ? 'text-green-600'
                                        : 'text-red-600'
                                }`}>
                                    {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
                                </span>
                            ) : null
                                ) : null}
                            </div>

                    {/* Color Options Preview */}
                    <div className="flex flex-col gap-1">
                        {(() => {
                            const uniqueColors = new Map<string, { color: string; id: number | string }>();
                            product.variations.forEach((variation) => {
                                if (variation.color && !uniqueColors.has(variation.color)) {
                                    uniqueColors.set(variation.color, {color: variation.color, id: variation.id});
                                }
                            });
                            const totalUniqueColors = uniqueColors.size;
                            const firstRow = Array.from(uniqueColors.values()).slice(0, 3);
                            const secondRow = Array.from(uniqueColors.values()).slice(3, 6);
                            const remaining = totalUniqueColors - 6;

                            return (
                                <>
                                    <div className="flex space-x-1">
                                        {firstRow.map(({color, id}) => {
                                            const hexColor = colorMap[color as keyof typeof colorMap] || '#6b7280';
                                            return (
                                                <button
                                                    key={id}
                                                    className="w-4 h-4 rounded-full border-2 border-gray-300 shadow-sm cursor-pointer hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                                    style={{backgroundColor: hexColor}}
                                                    title={color}
                                                    onMouseEnter={(e) => handleColorHover(e, color)}
                                                    onMouseLeave={handleColorLeave}
                                                    aria-label={`View ${product.name} in ${color}`}
                                                />
                                            );
                                        })}
                                    </div>
                                    {secondRow.length > 0 && (
                                        <div className="flex space-x-1 items-center">
                                            {secondRow.map(({color, id}) => {
                                                const hexColor = colorMap[color as keyof typeof colorMap] || '#6b7280';
                                                return (
                                                    <button
                                                        key={id}
                                                        className="w-4 h-4 rounded-full border-2 border-gray-300 shadow-sm cursor-pointer hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                                        style={{backgroundColor: hexColor}}
                                                        title={color}
                                                        onMouseEnter={(e) => handleColorHover(e, color)}
                                                        onMouseLeave={handleColorLeave}
                                                        aria-label={`View ${product.name} in ${color}`}
                                                    />
                                                );
                                            })}
                                            {remaining > 0 && (
                                                <div className="text-xs text-gray-500 ml-1">
                                                    +{remaining}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            </div>
        </article>
    );
}
